import { rolloutDays, type RolloutDayKey, type RolloutKind } from 'fym-shared/src/music24/releaseRollout';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type { CreateKitAssetItem, ReleasePlanDay, ReleasePost } from '../../../../../redux/api/fymAPI.types';

/** The state chip of a post on the Plan tab (design f1 legend). */
export type PlanPostState = 'posted' | 'scheduled' | 'draft' | 'needsYou' | 'failed' | 'publishing';

/** The chip of a post status. `canceled` posts are not in the answer. */
export function planPostState(status: string): PlanPostState {
  switch (status) {
    case 'posted':
      return 'posted';
    case 'draft':
      return 'draft';
    case 'awaitingManualPost':
      return 'needsYou';
    case 'failed':
      return 'failed';
    case 'publishing':
      return 'publishing';
    default:
      // scheduled, rendering, ready: the post goes out at its time.
      return 'scheduled';
  }
}

export interface PlanCounts {
  posted: number;
  scheduled: number;
  drafts: number;
  gaps: number;
}

/** "4 posted · 5 scheduled · 2 drafts · 2 gaps" of the rollout head (design f1). */
export function planCounts(days: ReleasePlanDay[]): PlanCounts {
  const posts = days.flatMap((day) => day.posts);
  const states = posts.map((post) => planPostState(post.status));
  return {
    posted: states.filter((state) => state === 'posted').length,
    scheduled: states.filter((state) => state === 'scheduled' || state === 'needsYou' || state === 'publishing').length,
    drafts: states.filter((state) => state === 'draft').length,
    gaps: days.filter((day) => day.gap).length,
  };
}

/**
 * The calendar day of a rollout day: the day of the answer. The server moves a day whose planned day
 * had passed into the time that is left (fym-shared `scheduleRolloutSteps`), so every view reads
 * the same day.
 */
export function planDayOn(day: ReleasePlanDay): string {
  return day.date.slice(0, 10);
}

/** What one "Add drafts" click would make first: the missing kit assets of the days, once each. */
export interface PlanMakeNeeds {
  /** The Studio jobs that `makeMissing` queues (one per asset type and moment). */
  items: CreateKitAssetItem[];
  /** The asset types of those jobs, for the gap line. */
  themeIds: StudioThemeId[];
  /** A day needs an AI reel that is not made: the Studio makes it, not this click. */
  needsReel: boolean;
}

/** The make needs of some days (`makeMissing` queues the missing kit assets, never an AI reel). */
export function planMakeNeeds(days: ReleasePlanDay[], kind: RolloutKind): PlanMakeNeeds {
  const table = rolloutDays(kind);
  const items = new Map<string, CreateKitAssetItem>();
  let needsReel = false;
  days.forEach((day) => {
    if (day.isTask || day.asset === null || day.asset.status !== 'missing') {
      return;
    }
    if (day.asset.themeId === 'aiReel') {
      needsReel = true;
      return;
    }
    const moment = table.find((one) => one.key === day.key)?.asset?.moment;
    const themeId = day.asset.themeId as StudioThemeId;
    items.set(`${themeId}:${moment ?? ''}`, { themeId, params: moment === undefined ? {} : { moment } });
  });
  const list = [...items.values()];
  return { items: list, themeIds: list.map((item) => item.themeId), needsReel };
}

/** The keys of the gap days, as `POST .../rollout` takes them. */
export function gapDayKeys(days: ReleasePlanDay[]): RolloutDayKey[] {
  return days.filter((day) => day.gap).map((day) => day.key as RolloutDayKey);
}

/** The posts newest first are in the answer; the table shows the newest `limit` unless "Show all". */
export function visiblePosts(posts: ReleasePost[], showAll: boolean, limit = 5): ReleasePost[] {
  return showAll ? posts : posts.slice(0, limit);
}
